import { useEffect, useState } from 'react';

import { useHandoffTargets } from '../../../../hooks/use-handoffs';
import { P1CategoryBadge } from '../../components/badges/P1CategoryBadge';
import { EscalationChainCard } from '../../components/cards/EscalationChainCard';
import { DrawerBase } from '../../components/modals/DrawerBase';
import { useAssignConversation } from '../../hooks/use-assign-conversation';
import { useCloseP1, useEscalateP1, useHandleP1, useResolveP1 } from '../../hooks/use-p1-mutations';

import { P1_LEVEL_META } from './tela8-adapter';

import type { P1EscalationLevel } from '../../types';
import type { P1CardVM } from '../../view-models';
import type React from 'react';

export interface P1DetailDrawerProps {
  card: P1CardVM | null;
  /** p1-escalate-chain (Pro+) — esconde o override manual quando ausente. */
  canEscalate: boolean;
  /** conversation-assign — habilita "Substituir atendente" (Item 330). */
  canAssign?: boolean;
  /** Abre o drawer já no modo reassign (entrada direta pelo botão do card). */
  initialMode?: 'view' | 'reassign';
  onClose: () => void;
}

type Mode = 'view' | 'resolve' | 'escalate' | 'reassign';

/**
 * Drawer de detalhe do P1 (D6) — surface do ciclo de vida que o card não expõe:
 * Tratar (handle) / Resolver (resolution) / Fechar (close) / Escalar (override
 * manual, gated). Reusa DrawerBase. Guard anti duplo-submit + role=alert em falha;
 * fecha no sucesso (a invalidação atualiza a lista).
 */
export function P1DetailDrawer({
  card,
  canEscalate,
  canAssign = false,
  initialMode = 'view',
  onClose,
}: P1DetailDrawerProps): React.JSX.Element | null {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [resolution, setResolution] = useState('');
  const [toLevel, setToLevel] = useState<P1EscalationLevel | ''>('');
  const [justification, setJustification] = useState('');
  const [reassignToUserId, setReassignToUserId] = useState('');
  const [reassignReason, setReassignReason] = useState('');

  const handleM = useHandleP1();
  const resolveM = useResolveP1();
  const closeM = useCloseP1();
  const escalateM = useEscalateP1();
  const assignM = useAssignConversation();
  const targets = useHandoffTargets();

  // Ao trocar de incidente (ou reabrir em modo direto), volta ao modo de entrada.
  const cardId = card?.id;
  useEffect(() => {
    setMode(initialMode);
    setResolution('');
    setToLevel('');
    setJustification('');
    setReassignToUserId('');
    setReassignReason('');
  }, [cardId, initialMode]);

  if (card === null) return null;

  const id = card.id;
  const pending =
    handleM.isPending ||
    resolveM.isPending ||
    closeM.isPending ||
    escalateM.isPending ||
    assignM.isPending;
  const errored = handleM.isError || resolveM.isError || closeM.isError || escalateM.isError;

  const done = (): void => {
    setMode('view');
    setResolution('');
    setToLevel('');
    setJustification('');
    setReassignToUserId('');
    setReassignReason('');
    onClose();
  };

  // Níveis acima do atual (escalonamento só sobe).
  const higherLevels: P1EscalationLevel[] = (
    ['attendant', 'supervisor', 'manager', 'director'] as P1EscalationLevel[]
  ).filter((l, i) => i > ['attendant', 'supervisor', 'manager', 'director'].indexOf(card.level));

  return (
    <DrawerBase
      open
      onClose={onClose}
      title={`Incidente crítico — Conversa #${card.conversationId}`}
      width={460}
    >
      <div className="modal-form">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <P1CategoryBadge category={card.category} />
          <span className="tweaks-hint">
            {card.statusLabel} · nível {P1_LEVEL_META[card.level].label} · confiança{' '}
            {card.confidencePct}%
          </span>
        </div>

        <EscalationChainCard chain={card.chain} />

        {errored && (
          <div className="modal-form__callout modal-form__callout--danger" role="alert">
            Não foi possível concluir a ação. Tente novamente.
          </div>
        )}

        {mode === 'view' && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              className="btn-ghost-mini"
              type="button"
              disabled={pending}
              onClick={() => handleM.mutate(id, { onSuccess: done })}
            >
              Tratar
            </button>
            <button
              className="btn-ghost-mini"
              type="button"
              disabled={pending}
              onClick={() => {
                setMode('resolve');
              }}
            >
              Resolver
            </button>
            <button
              className="btn-ghost-mini"
              type="button"
              disabled={pending}
              onClick={() => closeM.mutate(id, { onSuccess: done })}
            >
              Fechar
            </button>
            {canEscalate && higherLevels.length > 0 && (
              <button
                className="btn-ghost-mini"
                type="button"
                disabled={pending}
                onClick={() => {
                  setMode('escalate');
                }}
              >
                Escalar nível
              </button>
            )}
            {canAssign && (
              <button
                className="btn-ghost-mini"
                type="button"
                disabled={pending}
                onClick={() => {
                  setMode('reassign');
                }}
              >
                Substituir atendente
              </button>
            )}
          </div>
        )}

        {mode === 'reassign' && (
          <div>
            {/* Item 330 — reatribui a CONVERSA do incidente (reusa o assign do supervisor).
                O incidente segue aberto e a conversa segue p0 — não cria dono no P1. */}
            <label className="modal-form__label" htmlFor="p1-reassign-att">
              Novo atendente
            </label>
            <select
              id="p1-reassign-att"
              className="modal-form__select"
              value={reassignToUserId}
              disabled={pending || targets.isLoading}
              onChange={(e) => {
                setReassignToUserId(e.target.value);
              }}
            >
              <option value="">
                {targets.isLoading ? 'Carregando…' : 'Selecione um atendente…'}
              </option>
              {targets.data.map((t) => (
                <option key={t.userId} value={t.userId}>
                  {t.name}
                </option>
              ))}
            </select>
            <label className="modal-form__label" htmlFor="p1-reassign-reason">
              Motivo (opcional)
            </label>
            <textarea
              id="p1-reassign-reason"
              className="modal-form__textarea"
              rows={2}
              maxLength={1000}
              value={reassignReason}
              onChange={(e) => {
                setReassignReason(e.target.value);
              }}
              placeholder="Ex.: atendente sobrecarregado; passar ao especialista do caso."
            />
            {assignM.isError && (
              <div className="modal-form__callout modal-form__callout--danger" role="alert">
                Não foi possível reatribuir — o atendente pode estar fora do seu escopo de
                supervisão.
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                className="btn-ghost-mini"
                type="button"
                onClick={() => {
                  setMode('view');
                }}
              >
                Voltar
              </button>
              <button
                className="btn-ghost-mini btn-ghost-mini--primary"
                type="button"
                disabled={pending || reassignToUserId === ''}
                onClick={() => {
                  if (reassignToUserId === '') return;
                  assignM.mutate(
                    {
                      conversationId: card.conversationId,
                      assignToUserId: reassignToUserId,
                      ...(reassignReason.trim() !== '' ? { reason: reassignReason.trim() } : {}),
                    },
                    { onSuccess: done },
                  );
                }}
              >
                {assignM.isPending ? 'Reatribuindo…' : 'Confirmar substituição'}
              </button>
            </div>
          </div>
        )}

        {mode === 'resolve' && (
          <div>
            <label className="modal-form__label" htmlFor="p1-resolution">
              Desfecho (mín. 3 caracteres)
            </label>
            <textarea
              id="p1-resolution"
              className="modal-form__textarea"
              rows={3}
              value={resolution}
              onChange={(e) => {
                setResolution(e.target.value);
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                className="btn-ghost-mini"
                type="button"
                onClick={() => {
                  setMode('view');
                }}
              >
                Voltar
              </button>
              <button
                className="btn-ghost-mini btn-ghost-mini--primary"
                type="button"
                disabled={pending || resolution.trim().length < 3}
                onClick={() =>
                  resolveM.mutate({ id, resolution: resolution.trim() }, { onSuccess: done })
                }
              >
                {resolveM.isPending ? 'Resolvendo…' : 'Confirmar resolução'}
              </button>
            </div>
          </div>
        )}

        {mode === 'escalate' && (
          <div>
            <label className="modal-form__label" htmlFor="p1-tolevel">
              Escalar para
            </label>
            <select
              id="p1-tolevel"
              className="modal-form__select"
              value={toLevel}
              onChange={(e) => {
                setToLevel(e.target.value as P1EscalationLevel);
              }}
            >
              <option value="">Selecione o nível…</option>
              {higherLevels.map((l) => (
                <option key={l} value={l}>
                  {P1_LEVEL_META[l].label}
                </option>
              ))}
            </select>
            <label className="modal-form__label" htmlFor="p1-justification">
              Justificativa (mín. 3 caracteres)
            </label>
            <textarea
              id="p1-justification"
              className="modal-form__textarea"
              rows={3}
              value={justification}
              onChange={(e) => {
                setJustification(e.target.value);
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                className="btn-ghost-mini"
                type="button"
                onClick={() => {
                  setMode('view');
                }}
              >
                Voltar
              </button>
              <button
                className="btn-ghost-mini btn-ghost-mini--primary"
                type="button"
                disabled={pending || toLevel === '' || justification.trim().length < 3}
                onClick={() => {
                  if (toLevel === '') return;
                  escalateM.mutate(
                    { id, toLevel, justification: justification.trim() },
                    { onSuccess: done },
                  );
                }}
              >
                {escalateM.isPending ? 'Escalando…' : 'Confirmar escalação'}
              </button>
            </div>
          </div>
        )}
      </div>
    </DrawerBase>
  );
}
